import { z } from 'zod';
/** Every kind of control surface the guest app knows how to render. */
export const ControlKind = z.enum([
    'light',
    'switch',
    'climate',
    'cover',
    'media',
    'lock',
    'alarm',
    'button',
    'fan',
    'vacuum',
    'camera',
    'sensor',
    'binary_sensor',
    'select',
    'number',
]);
/**
 * Feature groups are the unit of permission granting. An invite grants a set of
 * these per room, so the admin never has to tick 40 individual entities.
 */
export const FeatureGroup = z.enum([
    'lighting',
    'climate',
    'covers',
    'media',
    'sensors',
    'cameras',
    'other',
]);
export const FEATURE_GROUPS = FeatureGroup.options;
export const FEATURE_GROUP_LABELS = {
    lighting: 'Lighting',
    climate: 'Heating & cooling',
    covers: 'Blinds & curtains',
    media: 'Music & TV',
    sensors: 'Sensors',
    cameras: 'Cameras',
    other: 'Other devices',
};
/**
 * Shown once at the top of a feature group's panel. Explains something the
 * controls themselves cannot — e.g. that this app deliberately has no music
 * browser, because casting from the guest's own phone is the point.
 */
export const GROUP_NOTES = {
    media: 'AirPlay or Spotify to play music on these speakers.',
};
/** Read-only groups can never mutate the house, so they are safe by default. */
export const READ_ONLY_GROUPS = ['sensors', 'cameras'];
export const Control = z.object({
    entityId: z.string(),
    name: z.string(),
    kind: ControlKind,
    group: FeatureGroup,
    /** Optional grouping label inside a room, e.g. "Ensuite". */
    zone: z.string().optional(),
    /** Rendering hint; a lamp and a ceiling light want different icons. */
    icon: z.string().optional(),
    /** Sensors only: pinned sensors show on the room summary card. */
    pinned: z.boolean().optional(),
    /**
     * The room's main light. Room cards expose a single on/off for every primary
     * control at once, so a guest can light a room without opening it; the rest
     * of the fittings live on the room's own page.
     */
    primary: z.boolean().optional(),
});
export const RoomKind = z.enum(['guest', 'shared', 'private', 'outdoor', 'utility']);
export const Room = z.object({
    id: z.string(),
    name: z.string(),
    kind: RoomKind,
    floor: z.enum(['ground', 'first', 'second', 'outside']),
    /** Short line shown under the room name on the rooms list. */
    blurb: z.string().optional(),
    icon: z.string(),
    controls: z.array(Control),
});
export const House = z.object({
    rooms: z.array(Room),
    /** Entities that live outside any single room. */
    alarmEntityId: z.string(),
    frontDoorLockEntityId: z.string(),
    /** Read-only contact, granted with the unlock privilege rather than a room. */
    frontDoorContactEntityId: z.string(),
    /** Optional extra locks/gates the admin can grant separately. */
    extraLocks: z.array(Control).default([]),
});
/** Which feature groups a room actually has anything in. */
export function roomGroups(room) {
    const seen = new Set();
    for (const c of room.controls)
        seen.add(c.group);
    return FEATURE_GROUPS.filter((g) => seen.has(g));
}
//# sourceMappingURL=house.js.map